<template>
  <!-- horizon rail (FI-1869): edge-attached frosted sidebar holding the FULL
       nav tree -- every major section as an expandable node with its pages
       indented beneath (v27 sidebars attach to the window edge; no floating
       island). tenant switcher at the top, collapse toggle at the foot; the
       account lives in the top bar (classic parity), and help lives BOTH
       here (round 13 section, incl. the fleet assistant launch row) and in
       the desktop top-bar menu. rows carry hover
       tooltips whose text comes from the registry desc lines. renders only
       under the horizon theme (gated in App.vue). -->
  <div>
    <div v-if="navState.mobileOpen" class="fi-sn-scrim" @click="closeMobileNav"></div>
    <!-- clicking bare rail background toggles the collapse, so the whole
         empty area works as a big hit target for it. onRailClick filters out
         anything that owns its own click; the foot button stays the keyboard-
         and screen-reader-accessible way to do this, which is why no role or
         tabindex goes on the nav landmark here. -->
    <nav
      id="fi-sn-drawer"
      ref="nav"
      class="fi-sn"
      :class="{ 'fi-sn--collapsed': railCollapsed, 'fi-sn--mobile-open': navState.mobileOpen }"
      aria-label="Main navigation"
      @click="onRailClick"
    >
      <!-- rail header: only the tenant switcher (or name plate) lives here,
           and only when the top bar is not already carrying it -- never both
           (barOwnsTenant, shared navState.railHidden observable). the
           collapse toggle moved to the rail FOOT (below): with the switcher
           in the bar on desktop, a header-anchored toggle left the rail
           topped by a lone stray button. single-tenant users have nothing
           to switch, so the bar shows nothing and the name plate stays. -->
      <div v-if="!railCollapsed && !barOwnsTenant" class="fi-sn-tenant">
        <select
          v-if="tenantOptions.length > 1"
          v-model="user.tenant"
          class="fi-sn-tenant-select"
          aria-label="Tenant"
          @change="onTenantChange"
        >
          <option v-for="opt in tenantOptions" :key="optValue(opt)" :value="optValue(opt)">{{ optText(opt) }}</option>
        </select>
        <div v-else class="fi-sn-tenant-label">{{ currentTenantLabel }}</div>
        <!-- staff-only tenant id copy. hover-reveal: a permanently visible
             second control squeezed the longest tenant names into an
             ellipsis -->
        <button
          v-if="ctx.isEffectiveAdmin"
          class="fi-sn-icon-btn fi-sn-copy"
          :title="'Copy tenant id (' + user.tenant + ')'"
          @click="copyTenant"
        >
          <i class="fa fa-clipboard" aria-hidden="true"></i>
        </button>
      </div>
      <!-- expanded: the full section tree. three tiers, each indented one
           step past its parent (section 45px / folder 57px / page 69px from
           the rail's left edge); depth is carried by indentation alone, the
           vertical guide hairlines having been removed 2026-07-30.
           an unlabeled registry group is NOT a missing heading -- it is loose
           pages sitting above the named folders, which is what every mixed
           section in the registry actually describes. -->
      <div v-if="!railCollapsed" class="fi-sn-tree">
        <!-- starred pages ride the very top, ahead of the fixed nav -->
        <template v-if="favoriteSection">
          <div class="fi-sn-node">
            <!-- the toolbar toggle rides the section title row, right-justified.
                 it has to be a SIBLING of the button, not inside it: a checkbox
                 nested in a button is invalid, and every click would also
                 collapse the section. it stays visible whether or not the
                 section is expanded, so the preference is reachable without
                 opening the list. -->
            <div class="fi-sn-sec-row">
              <button
                v-b-tooltip.hover.right="rowTip(favoriteSection.desc)"
                class="fi-sn-sec"
                :aria-expanded="isExpanded(favoriteSection.key) ? 'true' : 'false'"
                @click="onSectionToggle(favoriteSection.key)"
              >
                <i class="fa fi-sn-twisty" :class="isExpanded(favoriteSection.key) ? 'fa-angle-down' : 'fa-angle-right'" aria-hidden="true"></i>
                <i class="fa fi-sn-sec-icon fi-sn-sec-icon--star" :class="favoriteSection.icon" aria-hidden="true"></i>
                <span class="fi-sn-sec-label">{{ favoriteSection.label }}</span>
              </button>
              <label class="fi-sn-fav-bar-toggle fi-sn-fav-bar-toggle--inline">
                <input type="checkbox" v-model="navFavoritesBarEnabled" />
                Use Toolbar
              </label>
            </div>
            <div v-if="isExpanded(favoriteSection.key)" class="fi-sn-children">
              <div class="fi-sn-group">
                <div class="fi-sn-items">
                  <div v-for="fav in favoriteItems" :key="fav.key" class="fi-sn-fav-row">
                    <router-link
                      v-b-tooltip.hover.right="rowTip(fav.desc)"
                      :to="fav.to"
                      class="fi-sn-item fi-sn-item--fav"
                      :class="{ 'fi-sn-item--active': isItemActive(fav) }"
                      :aria-current="isItemActive(fav) ? 'page' : null"
                    >
                      <!-- favorites are registry rows, so each one arrives
                           carrying the same glyph it has in the tree -->
                      <i class="fa fi-sn-item-icon" :class="fav.icon || 'fa-circle-o'" aria-hidden="true"></i>
                      <span class="fi-sn-item-label">{{ fav.label }}</span>
                    </router-link>
                    <!-- unstar in place: without this the only way to drop a
                         favorite would be to navigate to the page first -->
                    <button
                      type="button"
                      class="fi-sn-fav-drop"
                      :title="'Remove ' + fav.label + ' from Favorites'"
                      :aria-label="'Remove ' + fav.label + ' from Favorites'"
                      @click="onUnfavorite(fav)"
                    ><i class="fa fa-star" aria-hidden="true"></i></button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="fi-sn-divider"></div>
        </template>

        <template v-for="(s, idx) in treeSections">
          <div v-if="idx === firstUtilityIndex" :key="s.key + '-divider'" class="fi-sn-divider"></div>
          <div :key="s.key" class="fi-sn-node">
            <!-- leaf section: the row itself navigates. the twisty column is
                 still reserved (empty) so leaf and branch labels line up -->
            <router-link
              v-if="!hasChildren(s)"
              v-b-tooltip.hover.right="rowTip(s.desc)"
              :to="s.landing"
              class="fi-sn-sec fi-sn-sec--leaf"
              :class="{ 'fi-sn-sec--current': s.key === activeSectionKey, 'fi-sn-sec--staff': s.staffOnly }"
              :aria-current="s.key === activeSectionKey ? 'page' : null"
            >
              <span class="fi-sn-twisty" aria-hidden="true"></span>
              <i class="fa fi-sn-sec-icon" :class="s.icon || 'fa-circle-o'" aria-hidden="true"></i>
              <span class="fi-sn-sec-label">{{ s.label }}</span>
            </router-link>
            <!-- branch section: the row toggles its subtree. the twisty leads
                 the row (a tree discloses on the left) instead of trailing it -->
            <button
              v-else
              v-b-tooltip.hover.right="rowTip(s.desc)"
              class="fi-sn-sec"
              :class="{ 'fi-sn-sec--current': s.key === activeSectionKey, 'fi-sn-sec--staff': s.staffOnly }"
              :aria-expanded="isExpanded(s.key) ? 'true' : 'false'"
              @click="onSectionToggle(s.key)"
            >
              <i class="fa fi-sn-twisty" :class="isExpanded(s.key) ? 'fa-angle-down' : 'fa-angle-right'" aria-hidden="true"></i>
              <i class="fa fi-sn-sec-icon" :class="s.icon || 'fa-circle-o'" aria-hidden="true"></i>
              <span class="fi-sn-sec-label">{{ s.label }}</span>
            </button>

            <div v-if="hasChildren(s) && isExpanded(s.key)" class="fi-sn-children">
              <div
                v-for="(group, gi) in railGroups(s)"
                :key="s.key + '-' + group.key"
                class="fi-sn-group"
                :class="{ 'fi-sn-group--seam': !group.label && gi > 0 }"
              >
                <!-- a named group is a folder row in ordinary row type, not a
                     floating uppercase eyebrow ranked below its own children -->
                <button
                  v-if="group.label"
                  class="fi-sn-fold"
                  :class="{ 'fi-sn-fold--staff': group.staffOnly }"
                  :aria-expanded="isGroupOpen(s.key, group.key) ? 'true' : 'false'"
                  @click="onGroupToggle(s.key, group.key)"
                >
                  <i class="fa fi-sn-twisty" :class="isGroupOpen(s.key, group.key) ? 'fa-angle-down' : 'fa-angle-right'" aria-hidden="true"></i>
                  <i v-if="group.icon" class="fa fi-sn-fold-icon" :class="group.icon" aria-hidden="true"></i>
                  <span class="fi-sn-fold-label">{{ group.label }}</span>
                </button>
                <div
                  v-if="!group.label || isGroupOpen(s.key, group.key)"
                  class="fi-sn-items"
                  :class="{ 'fi-sn-items--nested': !!group.label }"
                >
                  <template v-for="item in railItems(group)">
                    <a
                      v-if="item.href"
                      :key="item.key"
                      v-b-tooltip.hover.right="rowTip(item.desc)"
                      :href="item.href"
                      target="_blank"
                      rel="noopener"
                      class="fi-sn-item"
                      :class="{ 'fi-sn-item--staff': item.staffOnly }"
                    >
                      <i class="fa fi-sn-item-icon" :class="item.icon || 'fa-circle-o'" aria-hidden="true"></i>
                      <span class="fi-sn-item-label">{{ item.label }}</span>
                      <i class="fa fa-external-link fi-sn-ext" aria-hidden="true"></i>
                    </a>
                    <!-- action rows (round 13: the help section's fleet
                         assistant) fire an app event instead of routing -->
                    <button
                      v-else-if="item.action"
                      :key="item.key"
                      v-b-tooltip.hover.right="rowTip(item.desc)"
                      type="button"
                      class="fi-sn-item"
                      :class="{ 'fi-sn-item--active': isActionActive(item), 'fi-sn-item--staff': item.staffOnly }"
                      @click="onItemAction(item)"
                    >
                      <i class="fa fi-sn-item-icon" :class="item.icon || 'fa-circle-o'" aria-hidden="true"></i>
                      <span class="fi-sn-item-label">{{ item.label }}</span>
                    </button>
                    <router-link
                      v-else
                      :key="item.key"
                      v-b-tooltip.hover.right="rowTip(item.desc)"
                      :to="item.to"
                      class="fi-sn-item"
                      :class="{ 'fi-sn-item--active': isItemActive(item), 'fi-sn-item--staff': item.staffOnly }"
                      :aria-current="isItemActive(item) ? 'page' : null"
                    >
                      <i class="fa fi-sn-item-icon" :class="item.icon || 'fa-circle-o'" aria-hidden="true"></i>
                      <span class="fi-sn-item-label">{{ item.label }}</span>
                    </router-link>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- collapsed: icon strip. sections WITH children open a hover/tap
           flyout listing their pages (the compact rail now surfaces the whole
           subtree without expanding); leaf sections route straight to their
           page as before. a childless section with no landing (help when the
           help center is feature-disabled: only action/external rows remain)
           still counts as a branch, so those rows reach the user via its
           flyout instead of vanishing from the strip. -->
      <div v-else class="fi-sn-tree fi-sn-tree--icons">
        <template v-if="favoriteSection">
          <button
            type="button"
            class="fi-sn-icon-btn fi-sn-flyout-trigger fi-sn-icon-btn--star"
            :class="{ 'fi-sn-icon-btn--open': flyout && flyout.key === favoriteSection.key }"
            :aria-label="favoriteSection.label"
            aria-haspopup="menu"
            :aria-expanded="flyout && flyout.key === favoriteSection.key ? 'true' : 'false'"
            @pointerenter="onTriggerPointerEnter(favoriteSection, $event)"
            @pointerleave="onTriggerPointerLeave"
            @click="onTriggerClick(favoriteSection, $event)"
          >
            <i class="fa" :class="favoriteSection.icon" aria-hidden="true"></i>
          </button>
          <div class="fi-sn-divider fi-sn-divider--icons"></div>
        </template>
        <template v-for="(s, idx) in treeSections">
          <div v-if="idx === firstUtilityIndex" :key="s.key + '-divider'" class="fi-sn-divider fi-sn-divider--icons"></div>
          <button
            v-if="hasChildren(s)"
            :key="s.key"
            type="button"
            class="fi-sn-icon-btn fi-sn-flyout-trigger"
            :class="{ 'fi-sn-icon-btn--active': s.key === activeSectionKey, 'fi-sn-icon-btn--open': flyout && flyout.key === s.key }"
            :aria-label="s.label"
            aria-haspopup="menu"
            :aria-expanded="flyout && flyout.key === s.key ? 'true' : 'false'"
            @pointerenter="onTriggerPointerEnter(s, $event)"
            @pointerleave="onTriggerPointerLeave"
            @click="onTriggerClick(s, $event)"
          >
            <i class="fa" :class="s.icon || 'fa-circle-o'" aria-hidden="true"></i>
          </button>
          <router-link
            v-else-if="s.landing"
            :key="s.key"
            v-b-tooltip.hover.right="iconTip(s)"
            :to="s.landing"
            class="fi-sn-icon-btn"
            :class="{ 'fi-sn-icon-btn--active': s.key === activeSectionKey }"
            :aria-label="s.label"
            :aria-current="s.key === activeSectionKey ? 'page' : null"
          >
            <i class="fa" :class="s.icon || 'fa-circle-o'" aria-hidden="true"></i>
          </router-link>
        </template>
      </div>

      <!-- rail foot: the collapse toggle, pinned under the tree. desktop
           only -- the mobile drawer opens and closes from the top bar's
           hamburger, so a second toggle would be noise there. -->
      <div v-if="!navState.mobileOpen" class="fi-sn-foot">
        <button
          v-b-tooltip.hover.right="rowTip(navState.collapsed ? 'Expand navigation' : 'Collapse navigation')"
          class="fi-sn-icon-btn fi-sn-collapse-btn"
          :aria-label="navState.collapsed ? 'Expand navigation' : 'Collapse navigation'"
          @click="toggleCollapsed"
        >
          <!-- standard panel-left glyph: left column filled while the rail
               is open, outline only while collapsed -->
          <svg class="fi-sn-collapse-glyph" viewBox="0 0 20 16" aria-hidden="true">
            <rect x="1" y="1" width="18" height="14" rx="3" fill="none" stroke="currentColor" stroke-width="1.6"/>
            <line x1="7.5" y1="1.8" x2="7.5" y2="14.2" stroke="currentColor" stroke-width="1.6"/>
            <rect v-if="!navState.collapsed" x="2" y="2" width="4.6" height="12" rx="1.6" fill="currentColor" stroke="none"/>
          </svg>
        </button>
      </div>

    </nav>

    <!-- compact-rail flyout: a fixed popover anchored to the right of the
         hovered/tapped section icon, listing that section's items (the same
         rows the expanded tree shows). rendered OUTSIDE .fi-sn so the rail's
         backdrop-filter never becomes the containing block for its
         position:fixed. mirrors the top bar's menu popovers. -->
    <div
      v-if="flyout"
      ref="flyout"
      class="fi-sn-flyout"
      :style="{ top: flyout.top + 'px', left: flyout.left + 'px' }"
      role="menu"
      :aria-label="flyout.section.label"
      @pointerenter="onFlyoutPointerEnter"
      @pointerleave="onFlyoutPointerLeave"
      @keydown="onFlyoutKeydown"
      @focusout="onFlyoutFocusOut"
    >
      <!-- the menu already carries the section name via aria-label, so the
           visible title is decorative -- hidden from AT to keep role=menu's
           child model clean (menu owns only group/menuitem) -->
      <div class="fi-sn-flyout-title" aria-hidden="true">{{ flyout.section.label }}</div>
      <template v-for="group in flyout.section.groups">
        <div
          v-if="railItems(group).length"
          :key="flyout.section.key + '-' + group.key + '-grp'"
          role="group"
          :aria-label="group.label || flyout.section.label"
          class="fi-sn-flyout-group"
        >
          <div v-if="group.label" class="fi-sn-flyout-label">{{ group.label }}</div>
          <template v-for="item in railItems(group)">
            <a
              v-if="item.href"
              :key="item.key"
              :href="item.href"
              target="_blank"
              rel="noopener"
              role="menuitem"
              class="fi-sn-flyout-item"
              @click="closeFlyout"
            >
              <i class="fa fi-sn-flyout-icon" :class="item.icon || 'fa-circle-o'" aria-hidden="true"></i>
              <span class="fi-sn-flyout-text">{{ item.label }}</span>
              <i class="fa fa-external-link fi-sn-flyout-ext" aria-hidden="true"></i>
            </a>
            <button
              v-else-if="item.action"
              :key="item.key"
              type="button"
              role="menuitem"
              class="fi-sn-flyout-item"
              :class="{ 'fi-sn-flyout-item--active': isActionActive(item) }"
              @click="onFlyoutItemAction(item)"
            >
              <i class="fa fi-sn-flyout-icon" :class="item.icon || 'fa-circle-o'" aria-hidden="true"></i>
              <span class="fi-sn-flyout-text">{{ item.label }}</span>
            </button>
            <router-link
              v-else
              :key="item.key"
              :to="item.to"
              role="menuitem"
              class="fi-sn-flyout-item"
              :class="{ 'fi-sn-flyout-item--active': isItemActive(item) }"
              :aria-current="isItemActive(item) ? 'page' : null"
              @click.native="closeFlyout"
            >
              <i class="fa fi-sn-flyout-icon" :class="item.icon || 'fa-circle-o'" aria-hidden="true"></i>
              <span class="fi-sn-flyout-text">{{ item.label }}</span>
            </router-link>
          </template>
        </div>
      </template>
      <!-- last row of the favorites flyout specifically (not other
           sections') -- mirrors the expanded tree's own last-row checkbox -->
      <label
        v-if="favoriteSection && flyout.key === favoriteSection.key"
        class="fi-sn-fav-bar-toggle fi-sn-flyout-fav-toggle"
      >
        <input type="checkbox" v-model="navFavoritesBarEnabled" />
        Use Toolbar
      </label>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { buildNavContext, resolveNav, sectionKeyForRoute, resolveFavorites } from '@/nav/registry'
import { navState, setSectionExpanded, clearSectionExpanded, setGroupOpen, clearGroupOpen, toggleCollapsed, setMobileOpen, noHoverDevice } from '@/nav/nav_state'
import { favState, favKey, toggleFavorite, ensureFavoritesLoaded } from '@/nav/nav_favorites'
import { setNavFavoritesBarEnabled } from '@/nav/nav_favorites_bar'
import { siteMapFavoriteDestinations } from '@/utils/site_map_pages'
import { copyToClipboard } from '@/helpers/vsm_standalone_url'

// the favorites pseudo-section is not in the registry (its contents are the
// user's, not the product's), so it needs a key that no real section uses
const FAV_KEY = 'favorites'

export default {
  name: 'fi_section_nav',
  data() {
    return {
      navState,
      favState,
      // compact-rail flyout: { key, section, top, left } while open, else null.
      // the close timer and last-trigger element are non-reactive instance
      // fields (_flyoutCloseTimer / _flyoutTrigger), set in the methods below.
      flyout: null,
    }
  },
  mounted() {
    ensureFavoritesLoaded()
    // flyout dismissal: an outside press, Escape, or any scroll/resize (which
    // would strand the anchored position) closes it. capture-phase scroll
    // catches the rail tree + content pane, which don't bubble scroll.
    document.addEventListener('pointerdown', this.onDocPointerDown, true)
    document.addEventListener('keydown', this.onDocKeydown)
    window.addEventListener('scroll', this.onWinChange, true)
    window.addEventListener('resize', this.onWinChange)
  },
  beforeDestroy() {
    this.clearFlyoutTimer()
    document.removeEventListener('pointerdown', this.onDocPointerDown, true)
    document.removeEventListener('keydown', this.onDocKeydown)
    window.removeEventListener('scroll', this.onWinChange, true)
    window.removeEventListener('resize', this.onWinChange)
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
    }),
    ctx() {
      return buildNavContext(this.user, this.viewAs)
    },
    // read by favoriteSection's gate below, and by the "Use Toolbar" checkbox's
    // v-model -- same property serves both, so they can never disagree.
    navFavoritesBarEnabled: {
      get() {
        return !!this.$store.state.profile.nav_favorites_bar
      },
      set(value) {
        setNavFavoritesBarEnabled(value)
      },
    },
    nav() {
      return resolveNav(this.ctx)
    },
    treeSections() {
      // top sections first, help/settings/admin after the divider --
      // resolveNav already returns them in that order. contextual "you are
      // here" rows (notifications / profile, reached from the top-bar
      // chrome) render only while their page is the active route.
      return this.nav.filter(s => s.surfacing !== 'contextual' || this.isContextualActive(s))
    },
    // the user's starred destinations, re-gated on every resolve: a favorite
    // for a page they can no longer reach simply stops rendering. the site map
    // list is the second source behind the registry, for stars on pages this
    // rail has no row of its own for -- see resolveFavorites.
    favFallbacks() {
      return siteMapFavoriteDestinations(this.user, this.viewAs)
    },
    favoriteItems() {
      return resolveFavorites(this.nav, this.favState.keys, favKey, this.favFallbacks)
    },
    // shaped like a resolved section so the collapsed strip's flyout, which
    // reads section.groups + railItems(group), needs no special case.
    favoriteSection() {
      // hidden exactly while the favorites bar (fi_favorites_bar.vue) is
      // actually showing -- navState.railHidden covers the mobile case
      // (<991px), where the bar CSS-hides itself regardless of the
      // preference, so favorites don't just vanish from both places at once
      if (this.navFavoritesBarEnabled && !this.navState.railHidden) return null
      const items = this.favoriteItems
      if (!items.length) return null
      return {
        key: FAV_KEY,
        label: 'Favorites',
        icon: 'fa-star',
        desc: 'The pages you have starred.',
        groups: [{ key: 'favorites', label: null, items: items.map(i => ({ ...i, placement: 'rail' })) }],
      }
    },
    // the bar renders the switcher whenever it is not in its mobile
    // arrangement and there is more than one tenant -- fi_top_nav's
    // showTenantSelect, expressed against the same shared observable so the
    // two cannot disagree and show two dropdowns (or none)
    barOwnsTenant() {
      return !this.navState.railHidden && this.tenantOptions.length > 1
    },
    railCollapsed() {
      // the desktop rail can be collapsed to the icon strip; the mobile
      // drawer has no collapsed form -- it always presents the full tree
      // (collapsed is forced under 1600px, so without this every phone
      // opened the drawer onto the icon strip with no way out)
      return this.navState.collapsed && !this.navState.mobileOpen
    },
    firstUtilityIndex() {
      const idx = this.treeSections.findIndex(s => s.surfacing === 'utility')
      return idx > 0 ? idx : -1
    },
    activeSectionKey() {
      const fromRegistry = sectionKeyForRoute(this.ctx, this.$route.name)
      if (fromRegistry) {
        const sec = this.nav.find(s => s.key === fromRegistry)
        // a contextual hit only counts while its row actually renders: the
        // profile landing is query-matched to the viewer, so an admin on
        // ANOTHER user's page falls through to the settings path match
        if (!sec || sec.surfacing !== 'contextual' || this.isContextualActive(sec)) return fromRegistry
      }
      const path = this.$route.path || ''
      if (path.startsWith('/reports')) return 'reports'
      if (path.startsWith('/maps')) return 'maps'
      if (path.startsWith('/bookings')) return 'bookings'
      if (path.startsWith('/admin-tools')) return 'admin'
      if (path.startsWith('/settings') || path.startsWith('/tenant_config') || path.startsWith('/user')) return 'settings'
      return null
    },
    // '<sectionKey>:<groupKey>' of the folder holding the current page, or
    // null when the route is not a tree row (detail pages, deep links)
    activeGroupKey() {
      for (const s of this.nav) {
        for (const g of s.groups) {
          if (g.items.some(i => i.placement === 'rail' && i.to && this.isItemActive(i))) {
            return s.key + ':' + g.key
          }
        }
      }
      return null
    },
    viewAsNonAdmin() {
      return this.viewAs === 'non-admin'
    },
    tenantOptions() {
      // mirrors navbar visibleTenantOptions (navbar.vue:681-693): muggle
      // preview narrows to same-fleet tenants by id prefix
      const all = (this.user && this.user.tenant_options) || []
      if (!this.viewAsNonAdmin) return all
      const current = this.user && this.user.tenant
      if (!current || typeof current !== 'string') return all
      const prefix = current.split('-', 1)[0]
      if (!prefix) return all
      const sameFleet = all.filter(opt => {
        const v = typeof opt === 'object' ? opt.value : opt
        return typeof v === 'string' && v.split('-', 1)[0] === prefix
      })
      return sameFleet.length > 0 ? sameFleet : all
    },
    currentTenantLabel() {
      const hit = this.tenantOptions.find(opt => this.optValue(opt) === this.user.tenant)
      return hit ? this.optText(hit) : this.user.tenant
    },
  },
  watch: {
    // drilling into a page closes the mobile drawer; desktop collapse state
    // is intentionally NOT touched by navigation
    $route() {
      setMobileOpen(false)
      this.closeFlyout()
    },
    // the flyout only exists for the collapsed strip; if the rail expands
    // (toggle, or a viewport growing past the auto-collapse width) drop it
    railCollapsed(collapsed) {
      if (!collapsed) this.closeFlyout()
    },
    // arriving in a section always reveals its pages: a stale user-collapse
    // override would defeat the active-section rule in isExpanded, so drop
    // it. leaving a section needs no handling here -- active-rule expansion
    // evaporates on its own, and an explicit user-open sticks by design.
    activeSectionKey: {
      immediate: true,
      handler(key) {
        if (!key) return
        if (this.navState.expanded[key] === false) clearSectionExpanded(key)
      },
    },
    // same rule one tier down: a bookmarked deep link into a folder the user
    // had collapsed would otherwise render no row at all for the page they
    // are looking at
    activeGroupKey: {
      immediate: true,
      handler(key) {
        if (key) clearGroupOpen(key)
      },
    },
  },
  methods: {
    toggleCollapsed,
    // a click on bare rail background toggles the collapse. everything that
    // owns its own click is filtered out by walking up from the target --
    // rows, links, the tenant switcher, the foot button (which would
    // otherwise toggle twice) -- so only genuine empty space gets here.
    onRailClick(e) {
      // the mobile drawer has no collapsed form, so a toggle there is
      // invisible now and a surprise later, when the desktop rail turns out
      // to have been collapsed behind it
      if (this.navState.mobileOpen) return
      const el = e && e.target
      if (!el || typeof el.closest !== 'function') return
      // group headings are text, not empty space -- toggling the whole rail
      // from them reads as a misclick
      if (el.closest('a, button, select, input, label, .fi-sn-tenant, .fi-sn-group-label')) return
      // a drag that selects a label ends in a click on whatever is under the
      // pointer; collapsing the rail out from under that is not what was meant
      const sel = window.getSelection && window.getSelection()
      if (sel && !sel.isCollapsed) return
      toggleCollapsed()
    },
    // tri-state expansion (nav_state.expanded): explicit true/false overrides
    // win; with no override, only the active section renders open -- except
    // favorites, which the user built by hand and so opens by default
    isExpanded(key) {
      const v = this.navState.expanded[key]
      if (v == null) return key === this.activeSectionKey || key === FAV_KEY
      return !!v
    },
    // the toggle flips what the user SEES, not the raw stored bit -- closing
    // an active-rule-opened section must store an explicit false
    onSectionToggle(key) {
      setSectionExpanded(key, !this.isExpanded(key))
    },
    // groups that survived their gate AND have rows to show. the container
    // renders unconditionally per group so an unlabeled group still reads as
    // its own run of loose pages rather than merging into its neighbour.
    railGroups(s) {
      return s.groups.filter(g => this.railItems(g).length > 0)
    },
    // folders default OPEN; only an explicit collapse is stored
    isGroupOpen(sectionKey, groupKey) {
      const v = this.navState.groups[sectionKey + ':' + groupKey]
      return v == null ? true : !!v
    },
    onGroupToggle(sectionKey, groupKey) {
      setGroupOpen(sectionKey + ':' + groupKey, !this.isGroupOpen(sectionKey, groupKey))
    },
    onUnfavorite(fav) {
      toggleFavorite(fav.to)
    },
    // hover tooltips (bootstrap-vue): desc lines come from the registry,
    // which copies them from the overview/dashboard pages. an empty title
    // renders no tooltip, so rows without a desc opt out naturally.
    // suppressed on touch devices: a tap emulates hover and the tip sticks
    // open (branch rows keep the drawer open under it)
    rowTip(text) {
      if (noHoverDevice()) return { title: '' }
      return { title: text || '', delay: { show: 450, hide: 0 }, boundary: 'window', customClass: 'fi-sn-tip' }
    },
    iconTip(s) {
      if (noHoverDevice()) return { title: '' }
      // the collapsed strip hides labels, so the tooltip carries label + desc
      const title = s.desc
        ? '<div class="fi-sn-tip-label">' + s.label + '</div><div>' + s.desc + '</div>'
        : s.label
      return { title, html: !!s.desc, delay: { show: 300, hide: 0 }, boundary: 'window', customClass: 'fi-sn-tip' }
    },
    hasChildren(s) {
      return !s.landingOnly && s.groups.some(g => this.railItems(g).length > 0)
    },
    // contextual visibility matches the landing by route NAME (the help
    // center's ?tab= disambiguators must not hide the row) plus any query
    // keys the landing itself pins (the profile row pins ?username=<own>)
    isContextualActive(s) {
      if (!s.landing || this.$route.name !== s.landing.name) return false
      const lq = s.landing.query || {}
      const rq = this.$route.query || {}
      for (const k of Object.keys(lq)) {
        if (String(rq[k]) !== String(lq[k])) return false
      }
      return true
    },
    railItems(group) {
      return group.items.filter(i => i.placement === 'rail')
    },
    // ---- compact-rail flyout ----------------------------------------------
    // opens a fixed popover of the section's items to the right of the tapped
    // icon. anchored past the rail's right edge (measured, not the 56px
    // constant) so it never sits on top of the strip.
    openFlyout(section, triggerEl, focusFirst) {
      const navEl = this.$refs.nav
      const railRight = navEl ? navEl.getBoundingClientRect().right : triggerEl.getBoundingClientRect().right
      const r = triggerEl.getBoundingClientRect()
      // flush against the rail's right edge (no gap) so moving the pointer from
      // the icon into the flyout never crosses empty space that would drop it
      this.flyout = { key: section.key, section, top: r.top, left: railRight }
      this._flyoutTrigger = triggerEl
      this.$nextTick(() => {
        const el = this.$refs.flyout
        if (!el || !this.flyout) return
        // clamp so a long list opened near the viewport foot stays on screen
        const maxTop = window.innerHeight - el.offsetHeight - 8
        if (this.flyout.top > maxTop) this.flyout = { ...this.flyout, top: Math.max(8, maxTop) }
        if (focusFirst) {
          const first = el.querySelector('.fi-sn-flyout-item')
          if (first) first.focus()
        }
      })
    },
    onTriggerPointerEnter(section, evt) {
      // hover-open is for a real mouse only. touch/pen taps also fire
      // pointerenter (pointerType touch/pen) but must NOT hover-open -- the
      // trailing click would then toggle the just-opened flyout shut. this is
      // the case the old noHoverDevice() guard missed on hover-CAPABLE
      // touchscreens (surface / 2-in-1 / touch laptops) that report
      // (hover: hover); on those, touch opens through onTriggerClick instead.
      if (evt.pointerType && evt.pointerType !== 'mouse') return
      this.clearFlyoutTimer()
      this.openFlyout(section, evt.currentTarget, false)
    },
    onTriggerPointerLeave(evt) {
      if (evt.pointerType && evt.pointerType !== 'mouse') return
      this.scheduleFlyoutClose()
    },
    onTriggerClick(section, evt) {
      this.clearFlyoutTimer()
      // a branch icon has no page of its own (overview was dropped), so click
      // just toggles the menu -- and click covers keyboard Enter/Space and
      // every touch tap, opening with focus moved into the list
      if (this.flyout && this.flyout.key === section.key) this.closeFlyout()
      else this.openFlyout(section, evt.currentTarget, true)
    },
    onFlyoutPointerEnter(evt) {
      if (evt.pointerType && evt.pointerType !== 'mouse') return
      this.clearFlyoutTimer()
    },
    onFlyoutPointerLeave(evt) {
      if (evt.pointerType && evt.pointerType !== 'mouse') return
      this.scheduleFlyoutClose()
    },
    scheduleFlyoutClose() {
      // a grace period so the mouse can cross the ~11px of rail between the
      // icon and the (now flush) flyout without it closing. only ever armed by
      // the mouse-gated leave handlers.
      this.clearFlyoutTimer()
      this._flyoutCloseTimer = setTimeout(() => { this.flyout = null }, 250)
    },
    clearFlyoutTimer() {
      if (this._flyoutCloseTimer) {
        clearTimeout(this._flyoutCloseTimer)
        this._flyoutCloseTimer = null
      }
    },
    closeFlyout() {
      this.clearFlyoutTimer()
      this.flyout = null
    },
    closeFlyoutAndRefocus() {
      const t = this._flyoutTrigger
      this.closeFlyout()
      if (t && t.focus) t.focus()
    },
    onFlyoutItemAction(item) {
      this.onItemAction(item)
      this.closeFlyout()
    },
    onFlyoutKeydown(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.closeFlyoutAndRefocus(); return }
      const el = this.$refs.flyout
      if (!el) return
      const items = Array.prototype.slice.call(el.querySelectorAll('.fi-sn-flyout-item'))
      if (!items.length) return
      const cur = items.indexOf(document.activeElement)
      if (e.key === 'ArrowDown') { e.preventDefault(); this.focusItem(items[cur + 1] || items[0]) }
      else if (e.key === 'ArrowUp') { e.preventDefault(); this.focusItem(items[cur - 1] || items[items.length - 1]) }
      else if (e.key === 'Home') { e.preventDefault(); this.focusItem(items[0]) }
      else if (e.key === 'End') { e.preventDefault(); this.focusItem(items[items.length - 1]) }
    },
    focusItem(el) {
      if (!el) return
      // focus without the browser's auto scroll-into-view, then scroll the
      // flyout ourselves. a raw focus() on an off-screen item scrolls the
      // panel, and that scroll event would (pre-fix) dismiss the flyout.
      try { el.focus({ preventScroll: true }) } catch (_) { el.focus() }
      if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
    },
    onFlyoutFocusOut(e) {
      // focus leaving the menu entirely (Tab / Shift+Tab past an end) closes
      // it, so a keyboard user can't strand an open, highlighted popover.
      // moving between items keeps relatedTarget inside the flyout -> no close.
      const fly = this.$refs.flyout
      if (!fly) return
      const to = e.relatedTarget
      // a null relatedTarget is NOT a deliberate exit. webkit (ipad / ios and
      // macos safari) doesn't focus a link or button on press -- it blurs the
      // focused element instead, so pressing a row in a flyout we opened with
      // focusFirst (every touch tap goes through onTriggerClick) fired focusout
      // with no relatedTarget and unmounted the row mid-press, before its click
      // could dispatch: the icons opened, the links did nothing. staying open
      // costs nothing -- outside pointerdown, Escape, scroll/resize and the
      // rows' own handlers all still dismiss it.
      if (!to) return
      if (fly.contains(to) || (this._flyoutTrigger && this._flyoutTrigger.contains(to))) return
      this.closeFlyout()
    },
    onDocPointerDown(e) {
      if (!this.flyout) return
      const fly = this.$refs.flyout
      if (fly && fly.contains(e.target)) return
      if (this._flyoutTrigger && this._flyoutTrigger.contains(e.target)) return
      this.closeFlyout()
    },
    onDocKeydown(e) {
      if (!this.flyout || e.key !== 'Escape') return
      // only pull focus back to the trigger when it was actually inside the
      // menu (keyboard / click-open). a hover-open flyout leaves the user's
      // focus wherever it was -- e.g. a field they pressed Escape in.
      const fly = this.$refs.flyout
      if (fly && fly.contains(document.activeElement)) this.closeFlyoutAndRefocus()
      else this.closeFlyout()
    },
    onWinChange(e) {
      if (!this.flyout) return
      // the flyout's OWN inner scroll must not dismiss it: the fixed anchor
      // doesn't move when its list scrolls (target is the flyout itself).
      // only rail/content/page scroll (a different target) and resize do.
      if (e && e.type === 'scroll' && this.$refs.flyout && this.$refs.flyout.contains(e.target)) return
      this.closeFlyout()
    },
    isItemActive(item) {
      if (!item.to || this.$route.name !== item.to.name) return false
      // every key the item's query specifies must match the route...
      const iq = item.to.query || {}
      const rq = this.$route.query || {}
      for (const k of Object.keys(iq)) {
        if (String(rq[k]) !== String(iq[k])) return false
      }
      // loose rows own their whole multi-tab page (the help center row
      // stays lit on ?tab= deep links)
      if (item.loose) return true
      // ...and disambiguator keys present on the route must not be silently
      // ignored (else "Help Articles" also lights up on ?section=faq, and
      // "Event Log" on ?view=analytics)
      for (const k of ['source', 'section', 'tab', 'view']) {
        if (rq[k] != null && iq[k] == null) return false
      }
      return true
    },
    // action items fire an app event instead of routing (the help section's
    // fleet assistant row opens the panel via the launcher's $root listener).
    // closing the drawer mirrors what the $route watcher does for links.
    onItemAction(item) {
      setMobileOpen(false)
      this.$root.$emit(item.action)
    },
    // the assistant row reads active while the full-page assistant is the
    // route, so the tree still says "you are here" for an action row
    isActionActive(item) {
      return item.action === 'open-fleet-assistant' && this.$route.name === 'fleet_assistant'
    },
    optValue(opt) {
      return typeof opt === 'object' ? opt.value : opt
    },
    optText(opt) {
      return typeof opt === 'object' ? opt.text : opt
    },
    onTenantChange() {
      localStorage.setItem('selected_tenant', this.user.tenant)
      // mirror navbar set_tenant_id (navbar.vue:484-499): keep an in-route
      // ?tenant= query honest and drop cross-tenant detail ids
      const q = this.$route.query
      if (q && q.tenant && q.tenant !== this.user.tenant) {
        const next = { ...q, tenant: this.user.tenant }
        delete next.event_id
        this.$router.replace({ query: next }).catch(() => {})
      }
    },
    copyTenant() {
      copyToClipboard(String(this.user.tenant))
    },
    closeMobileNav() {
      setMobileOpen(false)
    },
  },
}
</script>

<style scoped>
.fi-sn {
  /* fixed, not sticky: the component's wrapper div is exactly as tall as the
     rail, so sticky has no travel and scrolls away with the page */
  position: fixed;
  /* --fi-hz-top-offset is set on <html> by fi_favorites_bar.vue itself
     whenever it's mounted (its own live height) -- see the comment on
     .fi-hz-root in App.vue for the full picture. this rule only matters
     above the 991px breakpoint: the mobile drawer variant further down keeps
     its own hardcoded 52px, sitting under the top bar and over the favorites
     bar (which at that width is one scrolling row, not a wrapped block). */
  top: var(--fi-hz-top-offset, 52px);
  left: 0;
  bottom: 0;
  z-index: 1020;
  display: flex;
  flex-direction: column;
  width: 264px;
  /* App.vue's .container-fluid centers text app-wide; a tree is left-aligned */
  text-align: left;
  background: var(--fi-glass-bg);
  -webkit-backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  border-right: 1px solid var(--fi-glass-line);
  transition: width var(--fi-motion-quick);
}
.fi-sn--collapsed { width: 56px; }

.fi-sn-tenant-select {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 16px; /* ios safari zooms inputs under 16px */
  color: var(--fi-chrome-ink-strong);
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--fi-radius-sm);
  padding: 4px 2px;
}
.fi-sn-tenant-select:hover { border-color: var(--fi-glass-line); }
.fi-sn-tenant-label {
  flex: 1 1 auto;
  font-size: 14px;
  font-weight: 600;
  color: var(--fi-chrome-ink-strong);
  padding: 6px 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fi-sn-copy { opacity: 0.55; }
.fi-sn-copy:hover { opacity: 1; }

.fi-sn-tree {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 6px 6px 10px;
  /* slim quiet scrollbar */
  scrollbar-width: thin;
  scrollbar-color: var(--fi-chrome-scrollbar) transparent;
}
.fi-sn-tree::-webkit-scrollbar { width: 8px; }
.fi-sn-tree::-webkit-scrollbar-track { background: transparent; }
.fi-sn-tree::-webkit-scrollbar-thumb {
  background: var(--fi-chrome-scrollbar);
  border-radius: 4px;
  /* transparent border + padding-box clip = a 4px thumb with breathing room */
  border: 2px solid transparent;
  background-clip: padding-box;
}
/* glyph ink sized to read like the 16px fa glyphs on sibling icon buttons */
.fi-sn-collapse-glyph {
  display: block;
  width: 17.5px;
  height: 14px;
}
.fi-sn-tree--icons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 0;
}
.fi-sn-node { margin-bottom: 0; }
.fi-sn-divider {
  height: 1px;
  margin: 6px 8px;
  background: var(--fi-glass-line);
}
.fi-sn-divider--icons { width: 24px; margin: 6px 0; }

/* ---- the tree ------------------------------------------------------------
   the GLYPH is the tier marker, and nothing sits to its left but the control
   that belongs there: a twisty on rows that disclose, the active rail on page
   rows. no row reserves width for a marker it is not currently showing.
     section   icon x=18   label x=40   (tree pad 6 + twisty 12, + icon 16 + gap 6)
     folder    icon x=41   label x=60   (children pad 26 -> 32 + fold pad 9, + 14 + 5)
     page row  icon x=53   label x=72   (nested pad 12 -> 44 + row pad 9, + 14 + 5)
   loose pages (an unlabeled group) share the FOLDER tier exactly -- icon
   x=41, label x=60 -- because that is what they are: files beside the
   folders, not inside one. they used to miss it by 4px. (fold + item rows
   pad 9, not the tree's 6: the active rail pipe is 3px wide and sat flush
   against the glyph at 6 -- chaz, 2026-08-01.)
   each tier's guide hairline drops from its parent row's twisty centerline
   (section twisty x=12, folder twisty x=23), so the rails line up with the
   control that opens them.
   the steps are deliberately tight: a 12px ladder read clearly but spent
   ~69px of a 264px rail on empty gutter before a page label started, which
   is a quarter of the width gone to indent. 8px still separates the tiers
   (the guides and the weight change do the rest) and hands every page row
   13px of label back. ------------------------------------------------------- */

/* section rows: twisty + icon + label, left-aligned */
.fi-sn-sec {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 8px 6px 0;
  border: none;
  border-radius: var(--fi-radius-sm);
  background: transparent;
  /* !important: App.vue globals force steelblue on bare anchors */
  color: var(--fi-chrome-ink) !important;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: background-color var(--fi-motion-quick);
}
.fi-sn-sec:hover {
  background: var(--fi-chrome-hover-bg);
  color: var(--fi-chrome-ink-strong) !important;
  text-decoration: none;
}
/* disclosure column. reserved (empty) on leaf rows too, so every section
   label starts on the same x whether or not it has a subtree */
.fi-sn-twisty {
  flex: 0 0 12px;
  width: 12px;
  text-align: center;
  font-size: 12px;
  color: var(--fi-chrome-muted);
}
.fi-sn-sec-icon {
  flex: 0 0 16px;
  width: 16px;
  margin-right: 6px;
  text-align: center;
  font-size: 14px;
  color: var(--fi-chrome-muted);
}
.fi-sn-sec-icon--star { color: var(--fi-chrome-star); }
.fi-sn-sec-label {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* v27 cue: the active section keeps a colored icon so the tree reads at a
   glance; the page-level highlight lives on the item rows */
.fi-sn-sec--current { color: var(--fi-chrome-ink-strong) !important; }
.fi-sn-sec--current .fi-sn-sec-icon { color: var(--fi-chrome-accent); }
/* leaf sections ARE the page, so the row carries the item-style tint */
.fi-sn-sec--leaf.fi-sn-sec--current,
.fi-sn-sec--leaf.fi-sn-sec--current:hover {
  background: var(--fi-chrome-active-bg);
  color: var(--fi-chrome-accent-ink) !important;
}

/* subtree: indented past the section's icon column. the vertical guide
   hairline that used to drop from the section twisty was removed 2026-07-30
   (Chaz) -- indentation alone carries the depth, and the only vertical line
   left in the rail is now the SELECTION pipe on the active row, which makes it
   unambiguous. */
.fi-sn-children {
  position: relative;
  margin: 0 0 3px;
  padding-left: 26px;
}
/* an unlabeled group after the first has no name to separate it from what
   came before, so it earns the one hairline the labels do not need. today
   that is the help section's Fleet Assistant / Site Map / Terms / Privacy
   run -- nowhere else in the registry. */
.fi-sn-group--seam {
  margin-top: 5px;
  padding-top: 6px;
  border-top: 1px solid var(--fi-glass-line);
}
/* folder rows: ordinary 13px row type at the strongest ink, so the heading
   outranks its own children instead of being a 10.5px uppercase eyebrow
   ranked below them */
.fi-sn-fold {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  /* 9px, matching the page rows: a folder and a loose page are siblings, so
     their glyphs belong on the same x. the old 16px was left over from when
     the LABEL had to reach this tier on its own, and it stranded the twisty
     19px away from the icon it discloses. */
  padding: 5px 8px 5px 9px;
  border: none;
  border-radius: var(--fi-radius-sm);
  background: transparent;
  color: var(--fi-chrome-ink-strong) !important;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: background-color var(--fi-motion-quick);
}
.fi-sn-fold:hover { background: var(--fi-chrome-hover-bg); }
/* out of flow and immediately left of the glyph, the way the section row's
   twisty already sits -- a disclosure control belongs against the thing it
   opens, not marooned in the gutter */
.fi-sn-fold .fi-sn-twisty {
  position: absolute;
  left: -6px;
  top: 50%;
  transform: translateY(-50%);
}
/* folder glyph: one step in from the section icon, one step out from its own
   children's, so the icon column steps with the tier it belongs to */
.fi-sn-fold-icon {
  flex: 0 0 14px;
  width: 14px;
  margin-right: 5px;
  text-align: center;
  font-size: 12.5px;
  color: var(--fi-chrome-muted);
}
.fi-sn-fold-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* pages inside a named folder take the third tier. its guide hairline was
   removed with the section one above. */
.fi-sn-items--nested {
  position: relative;
  padding-left: 12px;
  margin-bottom: 2px;
}
/* item rows carry the classic shell-rail active language: steelblue left
   rail + pale blue tint + steelblue text (.ms-nav-item.active et al.) */
.fi-sn-item {
  position: relative;
  display: flex;
  align-items: center;
  /* 5px on the right, not 8: the icon column costs every row 19px of label,
     and the tree's own 6px pad plus the 8px scrollbar already leave a 19px
     gutter past this edge. buys back the longest report names for free.
     left 9px so the 3px active rail pipe gets daylight before the glyph. */
  padding: 4px 5px 4px 9px;
  border-radius: 0 var(--fi-radius-sm) var(--fi-radius-sm) 0;
  /* !important: App.vue globals force steelblue on bare anchors */
  color: var(--fi-chrome-text) !important;
  font-size: 13px;
  /* 500 = avenir medium on apple devices. arial (the windows fallback) ships
     no medium face, so windows keeps rendering regular -- intentional: the
     sections' 600 already reads bold there and 600 here would flatten the
     tiers (and erase the active row's weight step, which stays 600 below) */
  font-weight: 500;
  text-decoration: none;
  transition: background-color var(--fi-motion-quick), border-color var(--fi-motion-quick);
}
/* page glyph: the narrowest column of the three tiers, and the only one that
   repeats across a run of rows on purpose -- within a folder it indexes the
   SUBJECT (drivers vs vehicles) so the eye can skip to the right block
   without reading labels. see the icon note in nav/registry.js. */
.fi-sn-item-icon {
  flex: 0 0 14px;
  width: 14px;
  margin-right: 5px;
  text-align: center;
  font-size: 12px;
  color: var(--fi-chrome-muted);
}
/* the ellipsis moved off the row and onto the label when the row became a
   flex container: min-width 0 lets a flex child shrink below its text width */
.fi-sn-item-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fi-sn-item:hover {
  background: var(--fi-chrome-hover-bg);
  color: var(--fi-chrome-ink-strong) !important;
  text-decoration: none;
}
.fi-sn-item:hover .fi-sn-item-icon { color: var(--fi-chrome-ink-strong); }
/* the active rail rides OUTSIDE the row's flow, as a pseudo-element rather
   than a transparent border. as a border it charged EVERY row 3px of width
   for a marker only the active one ever shows, and the 9px that separated it
   from the glyph was dead on all the others too. the rail lands on exactly
   the same pixels it always did; the row just no longer pays for it. */
.fi-sn-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: transparent;
}
.fi-sn-item--active,
.fi-sn-item--active:hover {
  background: var(--fi-chrome-active-bg);
  /* accent-ink, not accent: steelblue text on the pale-blue wash is ~3.2:1 */
  color: var(--fi-chrome-accent-ink) !important;
  font-weight: 600;
}
.fi-sn-item--active::before { background: var(--fi-chrome-accent); }
/* the glyph may take the full accent where the label may not: 3:1 is the bar
   for non-text content, and it keeps the active row's cue from going flat */
.fi-sn-item--active .fi-sn-item-icon,
.fi-sn-item--active:hover .fi-sn-item-icon { color: var(--fi-chrome-accent); }

/* ---- staff-only rows (wizard purple) ------------------------------------
   rows the resolver marked staffOnly: visible to this viewer only because
   they are staff (the same gate fails for a customer-admin twin). the
   standing admin-cue purple makes it obvious on a screenshare which rows
   the customer cannot see. placed after the hover/active rules so the
   purple ink survives every state; the active wash and rail pipe go purple
   with it so an active staff row never half-speaks steelblue. */
.fi-sn-item--staff,
.fi-sn-item--staff:hover,
.fi-sn-item--staff.fi-sn-item--active,
.fi-sn-item--staff.fi-sn-item--active:hover { color: var(--fi-chrome-admin) !important; }
.fi-sn-item--staff .fi-sn-item-icon,
.fi-sn-item--staff:hover .fi-sn-item-icon,
.fi-sn-item--staff.fi-sn-item--active .fi-sn-item-icon,
.fi-sn-item--staff.fi-sn-item--active:hover .fi-sn-item-icon { color: var(--fi-chrome-admin); }
.fi-sn-item--staff.fi-sn-item--active::before { background: var(--fi-chrome-admin); }
.fi-sn-sec--staff,
.fi-sn-sec--staff:hover,
.fi-sn-sec--staff.fi-sn-sec--current,
.fi-sn-sec--leaf.fi-sn-sec--current.fi-sn-sec--staff,
.fi-sn-sec--leaf.fi-sn-sec--current.fi-sn-sec--staff:hover { color: var(--fi-chrome-admin) !important; }
.fi-sn-sec--staff .fi-sn-sec-icon,
.fi-sn-sec--staff.fi-sn-sec--current .fi-sn-sec-icon { color: var(--fi-chrome-admin); }
.fi-sn-fold--staff,
.fi-sn-fold--staff:hover { color: var(--fi-chrome-admin) !important; }
.fi-sn-fold--staff .fi-sn-fold-icon { color: var(--fi-chrome-admin); }

/* ---- favorites ----------------------------------------------------------- */
/* last row of the expanded section: a divider sets it apart from the
   starred-page rows above, right-aligned to read as a footer control rather
   than another entry in the list */
.fi-sn-fav-bar-toggle {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  margin: 2px 8px 4px 0;
  padding-top: 5px;
  border-top: 1px solid var(--fi-glass-line);
  font-size: 10.5px;
  font-weight: 600;
  color: var(--fi-chrome-muted);
  cursor: pointer;
}

/* the section title and the toolbar toggle share one row; the button keeps the
   remaining width so its hover band still reads as the row */
.fi-sn-sec-row {
  display: flex;
  align-items: center;
}

.fi-sn-sec-row .fi-sn-sec {
  flex: 1 1 auto;
  min-width: 0;
}

/* inline variant: no top divider (that belonged to the old last-row position)
   and no bottom margin, so it sits on the title's baseline row */
.fi-sn-fav-bar-toggle--inline {
  flex: 0 0 auto;
  margin: 0 8px 0 0;
  padding-top: 0;
  border-top: 0;
  white-space: nowrap;
}
.fi-sn-fav-bar-toggle input[type="checkbox"] {
  width: 12px;
  height: 12px;
  margin: 0;
  accent-color: var(--fi-chrome-star);
  cursor: pointer;
}
/* same control, inside the collapsed rail's flyout popover instead of the
   inline tree -- overrides spacing/divider to match .fi-sn-flyout's own
   14px row padding and divider token (.fi-sn-flyout-title uses the same) */
.fi-sn-flyout-fav-toggle {
  margin: 4px 0 0;
  padding: 7px 14px 2px;
  border-top-color: var(--fi-chrome-divider);
}

/* the row hosts an unstar button, so the link needs room on the right and
   the button needs a positioned parent */
.fi-sn-fav-row { position: relative; }
.fi-sn-item--fav { padding-right: 30px; }
.fi-sn-fav-drop {
  position: absolute;
  top: 50%;
  right: 2px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: var(--fi-radius-sm);
  background: transparent;
  color: var(--fi-chrome-star) !important;
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
  /* hover-reveal: a column of stars beside every favorite would just be
     noise, since everything in this section is starred by definition */
  opacity: 0;
  transition: opacity var(--fi-motion-quick), background-color var(--fi-motion-quick);
}
.fi-sn-fav-row:hover .fi-sn-fav-drop,
.fi-sn-fav-drop:focus-visible { opacity: 1; }
.fi-sn-fav-drop:hover { background: var(--fi-chrome-hover-bg-strong); }
/* touch has no hover, so the affordance has to be permanent there */
@media (hover: none) {
  .fi-sn-fav-drop { opacity: 0.75; }
}
/* trailing marker on external rows; flex-shrink 0 so a long label ellipsizes
   instead of squeezing it away */
.fi-sn-ext { flex: 0 0 auto; margin-left: 5px; font-size: 10px; opacity: 0.6; }
/* action rows are buttons wearing the item look; strip the native chrome
   (no `font` shorthand -- reboot already inherits family/size on buttons) */
button.fi-sn-item {
  width: 100%;
  /* the left border used to survive this reset because it WAS the active
     rail; the rail is a pseudo-element now, so nothing here needs a border */
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

/* header bar: 11px left inset = (56px collapsed rail - 34px button) / 2, so
   the collapse toggle centers on the collapsed icon column AND holds the
   exact same x when the rail is expanded. it is the first child in both
   states, so its y is fixed too. */
.fi-sn-tenant {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 8px 8px 8px 11px;
  border-bottom: 1px solid var(--fi-glass-line);
}
.fi-sn-collapse-btn { flex: 0 0 34px; }
/* rail foot: home of the collapse toggle. margin-top:auto pins it to the
   rail's bottom edge (the flex column's tree sits above); the 11px left
   inset = (56px collapsed rail - 34px button) / 2, so the button centers
   on the collapsed icon column and holds one x in both rail states. */
.fi-sn-foot {
  flex: 0 0 auto;
  margin-top: auto;
  padding: 8px 8px 8px 11px;
  border-top: 1px solid var(--fi-glass-line);
}
.fi-sn-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: var(--fi-radius-sm);
  background: transparent;
  color: var(--fi-chrome-ink) !important;
  font-size: 16px;
  cursor: pointer;
  text-decoration: none;
}
.fi-sn-icon-btn:hover { background: var(--fi-chrome-hover-bg-strong); color: var(--fi-chrome-ink-strong) !important; }
/* the strip's favorites trigger carries the tree star's gold. !important
   because the base icon-btn color needs it (anchor-color anchor trap) --
   gold deliberately survives hover (the bg is the feedback), while the
   --active rule below outranks this by source order when the flyout opens */
.fi-sn-icon-btn.fi-sn-icon-btn--star { color: var(--fi-chrome-star) !important; }
.fi-sn-icon-btn--active,
.fi-sn-icon-btn--active:hover { background: var(--fi-chrome-active-bg-strong); color: var(--fi-chrome-accent) !important; }
/* while its flyout is open the trigger stays lit so the origin reads clearly */
.fi-sn-icon-btn--open,
.fi-sn-icon-btn--open:hover { background: var(--fi-chrome-hover-bg-strong); color: var(--fi-chrome-ink-strong) !important; }
/* bootstrap's reboot draws a focus ring on plain :focus, which lingers after
   a mouse click (section rows kept it after toggling); keyboard focus only */
.fi-sn-sec:focus,
.fi-sn-fold:focus,
.fi-sn-item:focus,
.fi-sn-fav-drop:focus,
.fi-sn-icon-btn:focus {
  outline: none;
  box-shadow: none;
}
.fi-sn-sec:focus-visible,
.fi-sn-fold:focus-visible,
.fi-sn-item:focus-visible,
.fi-sn-fav-drop:focus-visible,
.fi-sn-icon-btn:focus-visible,
.fi-sn-tenant-select:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--fi-action-primary-ring);
}

/* compact-rail flyout: the collapsed strip's on-demand submenu. mirrors the
   top bar's menu popovers (.fi-tn-*-pop) so both chromes read the same, and
   consumes the shared chrome tokens so midnight adapts with no extra rules. */
.fi-sn-flyout {
  position: fixed;
  z-index: 1500;
  min-width: 190px;
  max-width: 280px;
  max-height: calc(100vh - 64px);
  overflow-y: auto;
  padding: 6px 0 8px;
  border-radius: var(--fi-radius-md);
  background: var(--fi-glass-bg-strong);
  border: 1px solid var(--fi-glass-line);
  box-shadow: var(--fi-glass-shadow);
  text-align: left;
  scrollbar-width: thin;
  scrollbar-color: var(--fi-chrome-scrollbar) transparent;
}
.fi-sn-flyout::-webkit-scrollbar { width: 8px; }
.fi-sn-flyout::-webkit-scrollbar-track { background: transparent; }
.fi-sn-flyout::-webkit-scrollbar-thumb {
  background: var(--fi-chrome-scrollbar);
  border-radius: 4px;
  border: 2px solid transparent;
  background-clip: padding-box;
}
.fi-sn-flyout-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--fi-chrome-ink-strong);
  padding: 5px 14px 7px;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--fi-chrome-divider);
  white-space: nowrap;
}
.fi-sn-flyout-label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fi-chrome-eyebrow);
  padding: 6px 14px 3px;
}
/* item rows share the tree's active language: accent left-rail + tint + text */
.fi-sn-flyout-item {
  display: flex;
  align-items: center;
  padding: 7px 14px;
  border-left: 3px solid transparent;
  /* !important: App.vue globals force steelblue on bare anchors */
  color: var(--fi-chrome-text) !important;
  font-size: 13px;
  /* matches the expanded tree's page rows (avenir medium; no-op on windows) */
  font-weight: 500;
  text-decoration: none;
  transition: background-color var(--fi-motion-quick), border-color var(--fi-motion-quick);
}
/* the collapsed rail's only view of a section's pages, so it carries the same
   glyphs the expanded tree does -- one destination, one icon, either way in */
.fi-sn-flyout-icon {
  flex: 0 0 15px;
  width: 15px;
  margin-right: 8px;
  text-align: center;
  font-size: 12.5px;
  color: var(--fi-chrome-muted);
}
.fi-sn-flyout-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fi-sn-flyout-item:hover {
  background: var(--fi-chrome-hover-bg);
  color: var(--fi-chrome-ink-strong) !important;
  text-decoration: none;
}
.fi-sn-flyout-item:hover .fi-sn-flyout-icon { color: var(--fi-chrome-ink-strong); }
.fi-sn-flyout-item--active,
.fi-sn-flyout-item--active:hover {
  background: var(--fi-chrome-active-bg);
  border-left-color: var(--fi-chrome-accent);
  /* accent-ink for the same reason the tree's active row uses it: plain
     accent on the pale-blue wash lands at ~3.2:1 */
  color: var(--fi-chrome-accent-ink) !important;
  font-weight: 600;
}
.fi-sn-flyout-item--active .fi-sn-flyout-icon,
.fi-sn-flyout-item--active:hover .fi-sn-flyout-icon { color: var(--fi-chrome-accent); }
button.fi-sn-flyout-item {
  width: 100%;
  border-top: none;
  border-right: none;
  border-bottom: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.fi-sn-flyout-ext { font-size: 10px; opacity: 0.6; }
.fi-sn-flyout-item:focus { outline: none; box-shadow: none; }
.fi-sn-flyout-item:focus-visible {
  outline: none;
  /* inset so the overflow:hidden panel can't clip an outset ring */
  box-shadow: inset 0 0 0 2px var(--fi-action-primary-ring);
}

.fi-sn-scrim {
  display: none;
}
@media (max-width: 991px) {
  .fi-sn { display: none; }
  .fi-sn--mobile-open {
    display: flex;
    position: fixed;
    /* sit below the persistent 52px top bar so its top-left toggle is never
       overlapped -- the same fixed-spot button opens and closes the drawer */
    top: 52px;
    left: 0;
    bottom: 0;
    width: 300px;
    height: auto;
    z-index: 2000;
  }
  .fi-sn-scrim {
    display: block;
    position: fixed;
    /* clear the top bar so the toggle stays live (not covered or dimmed) */
    inset: 52px 0 0 0;
    background: var(--fi-chrome-scrim);
    z-index: 1999;
  }
  /* phone touch targets: ~42px section rows, ~38px folder + item rows (44 is
     the hig ideal; the tree is dense, these are the compromise heights) */
  .fi-sn-sec { padding-top: 11px; padding-bottom: 11px; }
  .fi-sn-fold { padding-top: 9px; padding-bottom: 9px; }
  .fi-sn-item { padding-top: 9px; padding-bottom: 9px; }
  /* last rows clear the home indicator */
  .fi-sn-tree { padding-bottom: calc(8px + env(safe-area-inset-bottom)); }
}
</style>

<style>
/* bv tooltips mount at body level, so their styling can't be scoped */
.fi-sn-tip .tooltip-inner {
  max-width: 280px;
  text-align: left;
}
.fi-sn-tip .fi-sn-tip-label {
  font-weight: 700;
  margin-bottom: 2px;
}
</style>
